import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button, ButtonType } from 'components/common/Button.component';
import { TextInput } from 'components/common/TextInput.component';
import { KEY } from 'components/common/constants';
import {
    DATE_MASK,
    DIGITS_PER_DATE,
    extractDigits,
    formatMaskedDate,
    formatMaskedDigits,
} from 'components/common/calendar/calendarHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Which end of the range a field stands for. */
export type RangeEnd = 'start' | 'end';

interface CalendarFooterProps {
    startDate: Date;
    endDate: Date;
    onApply: () => void;
    onCancel: () => void;
    /** Turns typed 'ddmmyyyy' digits into a date, or null when they spell none. */
    resolveDate: (digits: string) => Date | null;
    /** One end of the range typed into its own field. */
    onDateTyped: (which: RangeEnd, date: Date) => void;
    /** Extra arguments fill the `%1`-style placeholders of the translated string. */
    translate?: (key: string, ...args: string[]) => string;
}

/**
 * What `raw` gained over the `shown` value it was edited from — the characters that
 * were just keyed in, wherever in the field the caret happened to be.
 */
const insertedInto = (shown: string, raw: string): string => {
    let head = 0;
    while (head < raw.length && head < shown.length && raw[head] === shown[head]) head += 1;
    let tail = 0;
    while (tail < raw.length - head && tail < shown.length - head
        && raw[raw.length - 1 - tail] === shown[shown.length - 1 - tail]) tail += 1;
    return raw.slice(head, raw.length - tail);
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * The two ends of the range, echoed under the calendar and editable in their own
 * right: both fields are in the tab order and take a date typed in the same
 * dd-mm-yyyy mask as the field the popup hangs from.
 */
export const CalendarFooter: React.FC<CalendarFooterProps> = ({
    startDate,
    endDate,
    onApply,
    onCancel,
    resolveDate,
    onDateTyped,
    translate,
}) => {
    // Digits typed into one end, held only while that field is being edited. The
    // range itself stays with the calendar: a half-typed date is not a range yet,
    // but the field still has to show what has been keyed in so far.
    const [editing, setEditing] = useState<{ which: RangeEnd; digits: string } | null>(null);

    const t = (key: string, fallback: string, ...args: string[]) => translate?.(key, ...args) ?? fallback;

    const valueOf = (which: RangeEnd, date: Date) => editing?.which === which
        ? formatMaskedDigits(editing.digits)
        : formatMaskedDate(date);

    /**
     * The field holds nothing but digits, as the main one does: what is typed is
     * reduced to them and redrawn through the mask. Every keystroke that spells a
     * date moves that end of the range, so the calendar follows along.
     *
     * The date a field opens with counts as selected until the first keystroke: a
     * digit added to it starts a new date rather than running off the end of a mask
     * that is already full. Clicking into the field leaves the caret behind without
     * a selection, so this cannot be left to the browser.
     */
    const handleTyped = (which: RangeEnd, shown: string, raw: string) => {
        const started = editing?.which === which;
        const keyed = !started && raw.length > shown.length ? insertedInto(shown, raw) : raw;
        const digits = extractDigits(keyed).slice(0, DIGITS_PER_DATE);
        setEditing({ which, digits });
        const date = resolveDate(digits);
        if (date) onDateTyped(which, date);
    };

    /** Done typing: the field goes back to showing the date the calendar settled on. */
    const stopEditing = () => setEditing(null);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== KEY.ENTER) return;
        // Enter finishes the range here just as it does in the field above.
        event.preventDefault();
        stopEditing();
        onApply();
    };

    const dateField = (which: RangeEnd, date: Date) => {
        const shown = valueOf(which, date);
        return (
            <TextInput
                id={`calendar-footer-${which}-date`}
                value={shown}
                className="calendar-date-value"
                ariaLabel={which === 'start'
                    ? t('calendar.startDateInput', `Start date, format ${DATE_MASK}`, DATE_MASK)
                    : t('calendar.endDateInput', `End date, format ${DATE_MASK}`, DATE_MASK)}
                onChange={event => handleTyped(which, shown, event.target.value)}
                onKeyDown={handleKeyDown}
                // Highlight the date on the way in, so it is plain that typing replaces it.
                onFocus={event => event.target.select()}
                onBlur={stopEditing}
            />
        );
    };

    return (
        <div className="calendar-footer">
            <div className="calendar-footer-dates">
                {dateField('start', startDate)}
                <span className="calendar-footer-arrow" aria-hidden="true">
                    <ArrowRight size={16} />
                </span>
                {dateField('end', endDate)}
            </div>
            <div className="calendar-footer-actions">
                <Button
                    type={ButtonType.PRIMARY}
                    onClick={onApply}
                    text={t('calendar.applyDates', 'Apply dates')}
                />
                <Button
                    type={ButtonType.GHOST}
                    onClick={onCancel}
                    text={t('popUp.cancel', 'Cancel')}
                />
            </div>
        </div>
    );
};

export default CalendarFooter;
